// Navigationsmodell des neuen Designs: 5 Bereiche statt 22 Menüpunkte (siehe docs/DESIGN-KONZEPT.md).
// Reine Daten und Logik ohne React – getestet in navigation.test.js. Icons werden in der Shell zugeordnet.

/**
 * Ziel innerhalb eines Bereichs.
 * module: benötigtes Modulrecht (fehlt = für alle angemeldeten Nutzer)
 * flag:   zusätzlich nötige App-Einstellung (z. B. Easyhelp aktiv)
 * online: nur online nutzbar (Verwaltung)
 */
export const AREAS = [
  {
    key: 'start',
    label: 'Start',
    icon: 'home',
    items: [
      { path: '/', label: 'Übersicht', icon: 'home', keywords: 'home dashboard startseite' },
      { path: '/news-feed', label: 'News', icon: 'news', keywords: 'neuigkeiten nachrichten feed' },
      { path: '/leaderboard', label: 'Rangliste', icon: 'trophy', module: 'easyhelp', flag: 'leaderboard', keywords: 'leaderboard punkte' }
    ]
  },
  {
    key: 'teile',
    label: 'Teile',
    icon: 'search',
    items: [
      { path: '/search', label: 'Suche', icon: 'search', keywords: 'ersatzteile finden artikel' },
      { path: '/devices', label: 'Geräte', icon: 'devices', keywords: 'geräteauswahl kunde kasse' }
    ]
  },
  {
    key: 'bestellen',
    label: 'Bestellen',
    icon: 'cart',
    items: [
      { path: '/cart', label: 'Warenkorb', icon: 'cart', module: 'cart', keywords: 'bestellung ticket' },
      { path: '/dispatcher-orders', label: 'Disponenten', icon: 'clipboard', module: 'dispatcher-orders', keywords: 'disponenten-bestellung dispo' },
      { path: '/shipping-storage', label: 'Sammelkörbe', icon: 'inbox', module: 'shipping-storage', keywords: 'versandlager sammelkorb' }
    ]
  },
  {
    key: 'werkzeuge',
    label: 'Werkzeuge',
    icon: 'tools',
    items: [
      { path: '/documentation', label: 'Doku', icon: 'book', keywords: 'dokumentation anleitung handbuch' },
      { path: '/easyhelp', label: 'Easyhelp', icon: 'lifebuoy', module: 'easyhelp', keywords: 'hilfe support chat ticket' },
      { path: '/passwords', label: 'Passwörter', icon: 'key', module: 'passwords', keywords: 'passwort-datenbank zugangsdaten' }
    ]
  },
  {
    key: 'verwaltung',
    label: 'Verwaltung',
    icon: 'admin',
    hub: '/verwaltung',
    items: [
      { path: '/manage-parts', label: 'Teile', icon: 'wrench', module: 'manage-parts', online: true, description: 'Ersatzteile anlegen und pflegen', keywords: 'teile-verwaltung artikel' },
      { path: '/search-gaps', label: 'Suchen ohne Treffer', icon: 'searchX', module: 'search-gaps', online: true, description: 'Was Techniker suchen, aber nicht finden', keywords: 'fehlende teile katalog lücken keine ergebnisse' },
      { path: '/manage-devices', label: 'Geräte', icon: 'devices', module: 'manage-devices', online: true, description: 'Geräte und Zuordnungen', keywords: 'geräte-verwaltung' },
      { path: '/manage-customers', label: 'Kunden', icon: 'building', module: 'manage-customers', online: true, description: 'Kunden und Standorte', keywords: 'kunden-verwaltung firma' },
      { path: '/manage-categories', label: 'Kategorien', icon: 'folder', module: 'manage-categories', online: true, description: 'Kategorien für Teile und Geräte', keywords: 'kategorie-verwaltung' },
      { path: '/doc-generator', label: 'Doku-Verwaltung', icon: 'filePen', module: 'doc-generator', online: true, description: 'Dokumentationen erstellen und freigeben', keywords: 'dokumentations management generator' },
      { path: '/manage-doc-templates', label: 'Word-Vorlagen', icon: 'fileText', module: 'manage-doc-templates', online: true, description: 'Vorlagen für Dokumente', keywords: 'templates vorlage' },
      { path: '/document-archive', label: 'Dokumenten-Archiv', icon: 'archive', module: 'document_archive', online: true, description: 'Archivierte Dokumente', keywords: 'archiv paperless' },
      { path: '/shipping-methods', label: 'Versandwege', icon: 'truck', module: 'shipping-methods', online: true, description: 'Versandarten und Regeln', keywords: 'versand' },
      { path: '/manage-users', label: 'Benutzer', icon: 'users', module: 'manage-users', online: true, description: 'Konten, Rollen und Zugänge', keywords: 'benutzer-verwaltung nutzer mitarbeiter' },
      { path: '/permissions', label: 'Berechtigungen', icon: 'shield', module: 'permissions-management', online: true, description: 'Rechte für Gruppen und Personen', keywords: 'rechte rollen' },
      { path: '/analytics', label: 'Aktivität & Analytics', icon: 'chart', module: 'analytics', online: true, description: 'Systemaktivitäten und Auswertungen', keywords: 'activity log statistik auswertung' },
      { path: '/maintenance', label: 'Wartung', icon: 'toolbox', module: 'maintenance', online: true, description: 'Wartungsaufgaben', keywords: 'wartung' }
    ]
  }
];

/** Ziele, die nicht im Menü stehen, aber in der Suche auffindbar sind */
export const EXTRA_DESTINATIONS = [
  { path: '/settings', label: 'Einstellungen', icon: 'settings', keywords: 'profil passwort konto darstellung design dunkel backup' }
];

/**
 * Sichtbare Bereiche für einen Nutzer.
 * @param {(module:string) => boolean} can  Modulrecht vorhanden?
 * @param {Record<string, boolean>} flags  App-Einstellungen (leaderboard)
 */
export function buildNavigation(can, flags = {}) {
  return AREAS
    .map((area) => ({
      ...area,
      items: area.items.filter((item) => (!item.module || can(item.module)) && (!item.flag || flags[item.flag]))
    }))
    .filter((area) => area.items.length > 0)
    .map((area) => ({ ...area, path: area.hub || area.items[0].path }));
}

/** Bereich und Eintrag zur aktuellen Adresse (längster passender Pfad gewinnt) */
export function findLocation(areas, pathname) {
  let best = null;
  for (const area of areas) {
    if (area.hub && pathname === area.hub) return { area, item: null };
    for (const item of area.items) {
      const matches = item.path === '/' ? pathname === '/' : pathname === item.path || pathname.startsWith(`${item.path}/`);
      if (matches && (!best || item.path.length > best.item.path.length)) best = { area, item };
    }
  }
  return best;
}

const normalize = (text) => String(text ?? '').toLocaleLowerCase('de').normalize('NFKD').replace(/[̀-ͯ]/g, '');

/** Seiten für die globale Suche (Strg+K): alle Begriffe müssen in Name, Bereich oder Stichworten vorkommen */
export function searchDestinations(areas, query, extra = EXTRA_DESTINATIONS) {
  const terms = normalize(query).split(/\s+/).filter(Boolean);
  const all = [
    ...areas.flatMap((area) => area.items.map((item) => ({ ...item, area: area.label }))),
    ...extra.map((item) => ({ ...item, area: '' }))
  ];
  if (terms.length === 0) return all;
  return all
    .map((item) => {
      const label = normalize(item.label);
      const haystack = `${label} ${normalize(item.area)} ${normalize(item.keywords)} ${normalize(item.description)}`;
      if (!terms.every((term) => haystack.includes(term))) return null;
      // Treffer im Namen zuerst, Namensanfang ganz vorne
      const score = label.startsWith(terms[0]) ? 0 : label.includes(terms[0]) ? 1 : 2;
      return { item, score };
    })
    .filter(Boolean)
    .sort((a, b) => a.score - b.score)
    .map(({ item }) => item);
}
